import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';

interface ChangePasswordDialogProps {
    open: boolean;
    onClose: () => void;
    username: string;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
    open,
    onClose,
    username
}) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError(null);
        setSuccess(false);
        onClose();
    };

    const validatePassword = (password: string): string | null => {
        if (password.length < 6) {
            return 'Пароль должен содержать минимум 6 символов';
        }
        const englishPattern = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/;
        if (!englishPattern.test(password)) {
            return 'Пароль должен содержать только английские символы';
        }
        return null;
    };

    const handleSubmit = async () => {
        setError(null);

        // Validate current password is not empty
        if (!currentPassword) {
            setError('Введите текущий пароль');
            return;
        }

        // Validate new password
        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        // Check passwords match
        if (newPassword !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);

        try {
            // First verify current password
            const loginResponse = await axios.post('https://tbl.zerdegroup.kz/api/login', {
                login: username,
                password: currentPassword
            });

            if (!loginResponse.data.success) {
                setError('Неверный текущий пароль');
                setLoading(false);
                return;
            }

            // Then change password
            const resetResponse = await axios.post('https://tbl.zerdegroup.kz/api/password-reset/reset-password', {
                username,
                newPassword
            });

            if (resetResponse.data.success) {
                setSuccess(true);
                setTimeout(() => {
                    handleClose();
                }, 1500);
            } else {
                setError(resetResponse.data.message || 'Ошибка при смене пароля');
            }
        } catch (err) {
            console.error('Error changing password:', err);
            setError('Ошибка сервера при смене пароля');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    background: 'white',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 3,
                pb: 2
            }}>
                <Box sx={{ fontWeight: 600, fontSize: '1.25rem', color: '#09090b', letterSpacing: '-0.02em' }}>
                    Смена пароля
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: '#a1a1aa', '&:hover': { color: '#09090b', bgcolor: '#f4f4f5' } }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1, pb: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', bgcolor: '#fef2f2', color: '#991b1b' }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: '8px', bgcolor: '#ecfdf5', color: '#065f46' }}>
                        Пароль успешно изменен!
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Текущий пароль"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={loading || success}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '8px' }
                        }}
                    />

                    <TextField
                        label="Новый пароль"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={loading || success}
                        helperText="Минимум 6 символов, только английские буквы и цифры"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '8px' }
                        }}
                    />

                    <TextField
                        label="Подтвердите новый пароль"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={loading || success}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '8px' }
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: '#71717a', fontWeight: 500, textTransform: 'none' }}
                >
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || success}
                    disableElevation
                    sx={{
                        bgcolor: '#18181b',
                        color: 'white',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3,
                        '&:hover': {
                            bgcolor: '#27272a'
                        },
                        '&:disabled': {
                            bgcolor: '#e4e4e7',
                            color: '#a1a1aa'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Сменить пароль'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordDialog;
